import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import uuid from 'react-uuid';
import { ReactComponent as DownloadIcon } from '../../assets/images/download.svg';
import Button from '../../components/Elements/button/button';
import PageHeader from '../../components/page-header';
import { renderTemplate, withStaticImages, ZERVIO_HELPER_NAMES } from '../../helpers/handlebars-helpers';
import { addToast } from '../../store/features/toastSlice';
import {
  generateTemplateVersionPdf,
  getTemplateDetails,
  getTemplateVersionDetails,
  publishTemplateVersion,
  setTemplateVersionDetails,
  updateTemplateVersion,
} from '../../store/features/templatesSlice';
import { paperSize } from './paper';
import { TemplateEditorWrapper } from './template-editor.styled';

/** Re-rendering on every keystroke would recompile the template; this is imperceptible but cheap. */
const PREVIEW_DEBOUNCE_MS = 250;

/**
 * The test event is editor-only scratch data - templates-service has nowhere to keep it - so it
 * lives in this browser, keyed by version.
 */
const eventStorageKey = versionId => `template-editor:event:${versionId}`;

const readStoredEvent = versionId => {
  try {
    return window.localStorage.getItem(eventStorageKey(versionId)) || '';
  } catch (e) {
    return '';
  }
};

const writeStoredEvent = (versionId, value) => {
  try {
    window.localStorage.setItem(eventStorageKey(versionId), value);
  } catch (e) {
    // Private windows and blocked site data are fine here; the draft just isn't remembered.
  }
};

const TemplateEditor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: templateId, versionId } = useParams();
  const { templateDetails, templateVersionDetails } = useSelector(state => state.templates);

  const [html, setHtml] = useState('');
  const [eventText, setEventText] = useState('');
  const [debouncedHtml, setDebouncedHtml] = useState('');
  const [debouncedEvent, setDebouncedEvent] = useState('');
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const previewScrollRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);

  useEffect(() => {
    dispatch(getTemplateDetails({ id: templateId })).catch(() => {});
    dispatch(getTemplateVersionDetails({ templateId, versionId }))
      .then(version => {
        setHtml(version?.template_data || '');
        setEventText(readStoredEvent(versionId));
        setLoaded(true);
      })
      .catch(() => dispatch(addToast({ error: true, text: 'Could not load this template version', id: uuid() })));

    return () => {
      dispatch(setTemplateVersionDetails({}));
    };
  }, [templateId, versionId]);

  // Debounce what the preview compiles, not what the textareas show.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedHtml(html);
      setDebouncedEvent(eventText);
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [html, eventText]);

  const { event, eventError } = useMemo(() => {
    if (!eventText.trim()) return { event: {}, eventError: null };
    try {
      const parsed = JSON.parse(eventText);
      return { event: parsed, eventError: null };
    } catch (e) {
      return { event: null, eventError: e.message };
    }
  }, [debouncedEvent, eventText]);

  const { html: renderedHtml, error: templateError } = useMemo(() => {
    if (eventError) return { html: null, error: null };
    return renderTemplate(debouncedHtml, withStaticImages(event, templateVersionDetails?.images));
  }, [debouncedHtml, event, eventError, templateVersionDetails]);

  const page = useMemo(
    () =>
      paperSize(
        templateVersionDetails?.format || templateDetails?.format,
        templateVersionDetails?.landscape ?? templateDetails?.landscape,
      ),
    [templateVersionDetails, templateDetails],
  );

  // Fit the true-size page into whatever width the pane has.
  const fitPreview = useCallback(() => {
    const container = previewScrollRef.current;
    if (!container) return;
    const available = container.clientWidth - 32;
    setPreviewScale(available > 0 ? Math.min(1, available / page.width) : 1);
  }, [page.width]);

  useLayoutEffect(() => {
    fitPreview();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', fitPreview);
      return () => window.removeEventListener('resize', fitPreview);
    }
    const observer = new ResizeObserver(fitPreview);
    if (previewScrollRef.current) observer.observe(previewScrollRef.current);
    return () => observer.disconnect();
  }, [fitPreview]);

  const onHtmlChange = value => {
    setHtml(value);
    setDirty(true);
  };

  const onEventChange = value => {
    setEventText(value);
    writeStoredEvent(versionId, value);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await dispatch(updateTemplateVersion({ templateId, versionId, body: { template_data: html } }));
      setDirty(false);
      dispatch(addToast({ error: false, text: 'Template saved', id: uuid() }));
    } catch (e) {
      dispatch(addToast({ error: true, text: 'Could not save the template', id: uuid() }));
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    try {
      if (dirty) {
        await dispatch(updateTemplateVersion({ templateId, versionId, body: { template_data: html } }));
        setDirty(false);
      }
      await dispatch(publishTemplateVersion({ templateId, versionId }));
      await dispatch(getTemplateVersionDetails({ templateId, versionId }));
      dispatch(addToast({ error: false, text: 'Version published', id: uuid() }));
    } catch (e) {
      dispatch(addToast({ error: true, text: 'Could not publish this version', id: uuid() }));
    }
  };

  /**
   * Saves first, so the pdf is rendered from the body on screen rather than the last saved one.
   * The PDF comes back as a blob; an anchor click is what actually names the file.
   */
  const onDownloadPdf = async () => {
    if (eventError) {
      dispatch(addToast({ error: true, text: 'Fix the test event JSON before downloading', id: uuid() }));
      return;
    }
    setDownloading(true);
    try {
      if (dirty) {
        await dispatch(updateTemplateVersion({ templateId, versionId, body: { template_data: html } }));
        setDirty(false);
      }
      const blob = await dispatch(generateTemplateVersionPdf({ templateId, versionId, body: event }));
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(templateDetails?.name || 'template').replace(/[^\w\-. ]+/g, '')} v${
        templateVersionDetails?.version_no ?? ''
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      dispatch(addToast({ error: true, text: 'Could not generate the pdf', id: uuid() }));
    } finally {
      setDownloading(false);
    }
  };

  const isPublished = Boolean(templateVersionDetails?.published_on && !templateVersionDetails?.archived_on);

  const names = [
    { text: 'Templates', path: '/templates' },
    {
      text: templateDetails?.name,
      onClick: () => navigate(`/templates/${templateId}/versions`),
    },
    { text: `V${templateVersionDetails?.version_no ?? ''}`, onClick: () => {} },
  ];

  // srcDoc rather than dangerouslySetInnerHTML: the template ships its own <style>, and the PDF
  // renderer gives it a document of its own. An iframe reproduces that and keeps the template's
  // css out of the dashboard.
  const previewDocument = useMemo(() => {
    if (renderedHtml === null) return '';
    return /<html[\s>]/i.test(renderedHtml)
      ? renderedHtml
      : `<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0">${renderedHtml}</body></html>`;
  }, [renderedHtml]);

  return (
    <TemplateEditorWrapper className="flex-column flex-1">
      <PageHeader names={names} />

      <div className="editor-toolbar flex items-center justify-between px-6 py-3">
        <div className="flex-column">
          <label className="medium-text">{templateDetails?.name || 'Template'}</label>
          <label className="helper-hint">
            {`${page.width} × ${page.height} px · ${ZERVIO_HELPER_NAMES.length} helpers available`}
          </label>
        </div>
        <div className="flex items-center col-gap-4">
          {dirty && <label className="helper-hint mr-2">Unsaved changes</label>}
          <Button
            className={saving ? 'secondary disabled' : 'secondary'}
            size="medium"
            label={saving ? 'Saving…' : 'Save'}
            onClick={onSave}
          />
          <Button
            className={downloading || eventError ? 'secondary disabled' : 'secondary'}
            size="medium"
            icon={<DownloadIcon height={14} width={14} />}
            label={downloading ? 'Rendering…' : 'Download PDF'}
            onClick={onDownloadPdf}
          />
          <Button className="primary" size="medium" label="Publish" onClick={onPublish} />
        </div>
      </div>

      {isPublished && (
        <div className="published-warning mx-6 mt-4 pxy-3">
          <label className="regular-text">
            This version is published — saving changes what documents render right now. Duplicate it from the version
            list to work on a draft instead.
          </label>
        </div>
      )}

      <div className="editor-panes flex flex-1 px-6 py-4">
        <div className="flex-column editor-half" style={{ gap: '16px' }}>
          <div className="editor-pane flex-column" style={{ flex: '3 1 0' }}>
            <div className="pane-header flex items-center pxy-3">
              <label className="medium-text">Template</label>
              <label className="helper-hint ml-4">Handlebars HTML</label>
            </div>
            <textarea
              autoComplete="off"
              spellCheck={false}
              className="code-input scroll-text"
              placeholder={loaded ? '<html>…' : 'Loading…'}
              value={html}
              onChange={e => onHtmlChange(e.target.value)}
            />
            {templateError && (
              <div className="pane-error pxy-3">
                <label className="regular-text">{templateError}</label>
              </div>
            )}
          </div>

          <div className="editor-pane flex-column" style={{ flex: '2 1 0' }}>
            <div className="pane-header flex items-center pxy-3">
              <label className="medium-text">Test event</label>
              <label className="helper-hint ml-4">JSON, the data the template renders against</label>
            </div>
            <textarea
              autoComplete="off"
              spellCheck={false}
              className={`code-input scroll-text ${eventError ? 'invalid' : ''}`}
              placeholder={'{\n  "customer": { "name": "Ada" }\n}'}
              value={eventText}
              onChange={e => onEventChange(e.target.value)}
            />
            {eventError && (
              <div className="pane-error pxy-3">
                <label className="regular-text">{`Invalid JSON — ${eventError}`}</label>
              </div>
            )}
          </div>
        </div>

        <div className="editor-pane flex-column editor-half">
          <div className="pane-header flex items-center justify-between pxy-3">
            <label className="medium-text">Preview</label>
            <label className="helper-hint">{`${Math.round(previewScale * 100)}%`}</label>
          </div>
          <div className="preview-scroll flex-column items-center pxy-4" ref={previewScrollRef}>
            {eventError ? (
              <label className="helper-hint mt-10">Fix the test event JSON to see the preview.</label>
            ) : (
              // A scaled element keeps its unscaled layout box, so the page is wrapped in a
              // frame sized to the scaled result - otherwise centering pushes it out of view.
              <div
                className="preview-frame"
                style={{
                  width: `${Math.round(page.width * previewScale)}px`,
                  height: `${Math.round(page.height * previewScale)}px`,
                }}>
                <div
                  className="preview-page"
                  style={{
                    width: `${page.width}px`,
                    height: `${page.height}px`,
                    transform: `scale(${previewScale})`,
                  }}>
                  <iframe
                    title="Template preview"
                    sandbox=""
                    srcDoc={previewDocument}
                    width={page.width}
                    height={page.height}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </TemplateEditorWrapper>
  );
};

export default TemplateEditor;
